
import Vue from 'vue';
import Component from 'vue-class-component';
import {namespace} from 'vuex-class';
import {Watch} from 'vue-property-decorator';
import {IOnOffHandlers} from 'bonomo/dist';
import {IInvoice, IInvoiceOrder, IInvoiceMessage} from '@/model/Invoice/invoiceInterfaces';
import {IErrors} from '@/model/IErrors';
import Datim from '@/components/shared/Datim.vue';
import SimpleConfirmationDialog from '@/components/shared/SimpleConfirmationDialog.vue';
import {UrlHelper} from '@/helpers/UrlHelper';

const invoiceModule = namespace('invoiceModule');

@Component({
  components: {Datim, SimpleConfirmationDialog},
})
export default class Invoice extends Vue {
  @invoiceModule.Getter('invoice')
  protected invoice!: IInvoice;
  @invoiceModule.Getter('ordersForInvoicing')
  protected ordersForInvoicing!: IInvoiceOrder[];
  @invoiceModule.Getter('errors')
  protected errors!: IErrors;

  protected loading: boolean = true;
  protected submitting: boolean = false;
  protected loadingOrders: boolean = false;
  protected showPaymentDialog: boolean = false;
  protected showDeleteDialog: boolean = false;
  protected showOrderEditDialog: boolean = false;
  protected paymentAmount: string = '';
  protected paymentErrors: string[] = [];
  protected selectedOrders: IInvoiceOrder[] = [];
  protected draggedOrder: IInvoiceOrder | null = null;
  protected draggedFrom: string = '';
  protected dragOverTarget: string = '';
  private subscribes: IOnOffHandlers = {};

  protected orderHeaders = [
    {text: '#', value: 'id'},
    {text: 'Customer', value: 'user'},
    {text: 'Status', value: 'status'},
    {text: 'Total', value: 'grand_total'},
    {text: 'Date', value: 'created_at'},
  ];

  protected paymentHeaders = [
    {text: 'Amount', value: 'amount'},
    {text: 'Date', value: 'date_attempted'},
  ];

  protected messageHeaders = [
    {text: 'Type', value: 'type', sortable: false, width: '80px'},
    {text: 'Date', value: 'created_at', sortable: false, width: '160px'},
    {text: 'Status', value: 'status', sortable: false, width: '100px'},
    {text: 'Message', value: 'content', sortable: false},
  ];

  get invoiceId(): number {
    return parseInt(this.$route.params.invoiceId, 10);
  }

  get canSendEmail(): boolean {
    return !!this.invoice && this.invoice.status !== 'invoice_paid';
  }

  get canAddPayment(): boolean {
    return !!this.invoice && this.invoice.status !== 'invoice_paid';
  }

  get oldInvoiceUrl(): string {
    return UrlHelper.mayorInvoice(this.invoiceId);
  }

  get availableOrders(): IInvoiceOrder[] {
    const selectedIds = this.selectedOrders.map((o: IInvoiceOrder) => o.id);
    return this.ordersForInvoicing.filter((o: IInvoiceOrder) => selectedIds.indexOf(o.id) === -1);
  }

  @Watch('$route')
  onRouteChanged() {
    this.loadData();
  }

  created() {
    this.subscribes = (this as any).$ons({
      'backend.call.OK.invoiceLoaded': this.onLoaded,
      'backend.call.OK.invoiceDeleted': this.onDeleted,
      'backend.call.OK.ordersForInvoicingLoaded': this.onOrdersForInvoicingLoaded,
      'backend.call.KO': this.onError,
    });
    this.loadData();
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  protected statusColor(status: string): string {
    if (status === 'invoice_paid') return 'green';
    if (status === 'invoice_awaiting_payment') return 'orange';
    return 'grey';
  }

  protected messageStatusColor(status: string): string {
    if (status === 'sent') return 'green';
    if (status === 'received') return 'teal';
    if (status === 'error') return 'red';
    return 'grey';
  }

  protected orderStatusColor(status: string): string {
    if (status === 'status_order_completed') return 'green';
    if (status === 'status_order_client_cancelled' || status === 'status_order_sp_cancelled' || status === 'status_order_sp_rejected') return 'red';
    if (status === 'status_order_in_progress') return 'blue';
    return 'blue-grey';
  }

  protected onSendEmail() {
    this.submitting = true;
    this.$store.dispatch('invoiceModule/sendEmail', this.invoiceId);
  }

  protected onAddPayment() {
    if (!this.paymentAmount || parseFloat(this.paymentAmount) <= 0) {
      this.paymentErrors = [this.$t('invoices.errors.wrong_amount_specified').toString()];
      return;
    }
    this.paymentErrors = [];
    this.submitting = true;
    this.$store.dispatch('invoiceModule/addPayment', {
      invoiceId: this.invoiceId,
      amount: this.paymentAmount,
    });
  }

  protected onDelete() {
    this.submitting = true;
    this.$store.dispatch('invoiceModule/deleteInvoice', this.invoiceId);
  }

  protected onUpdateOrders() {
    this.submitting = true;
    const orderIds = this.selectedOrders.map((o: IInvoiceOrder) => o.id);
    this.$store.dispatch('invoiceModule/updateInvoice', {
      invoiceId: this.invoiceId,
      orderIds,
    });
  }

  protected onShowOrderEditDialog() {
    this.selectedOrders = [];
    this.loadingOrders = true;
    this.$store.dispatch('invoiceModule/fetchOrdersForInvoicing');
    this.showOrderEditDialog = true;
  }

  protected addToSelected(order: IInvoiceOrder) {
    this.selectedOrders = [...this.selectedOrders, order];
  }

  protected removeFromSelected(order: IInvoiceOrder) {
    this.selectedOrders = this.selectedOrders.filter((o: IInvoiceOrder) => o.id !== order.id);
  }

  protected onDragStart(order: IInvoiceOrder, from: string) {
    this.draggedOrder = order;
    this.draggedFrom = from;
  }

  protected onDragOver(e: DragEvent, target: string) {
    e.preventDefault();
    this.dragOverTarget = target;
  }

  protected onDragLeave(e: DragEvent) {
    const related = e.relatedTarget as Node | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    this.dragOverTarget = '';
  }

  protected onDrop(e: DragEvent, target: string) {
    e.preventDefault();
    this.dragOverTarget = '';
    if (!this.draggedOrder || this.draggedFrom === target) {
      this.draggedOrder = null;
      this.draggedFrom = '';
      return;
    }
    if (target === 'selected') {
      this.selectedOrders = [...this.selectedOrders, this.draggedOrder];
    } else {
      const dropped = this.draggedOrder;
      this.selectedOrders = this.selectedOrders.filter((o: IInvoiceOrder) => o.id !== dropped.id);
    }
    this.draggedOrder = null;
    this.draggedFrom = '';
  }

  protected onDragEnd() {
    this.draggedOrder = null;
    this.draggedFrom = '';
    this.dragOverTarget = '';
  }

  private loadData() {
    this.loading = true;
    this.$store.dispatch('invoiceModule/fetchInvoice', this.invoiceId);
  }

  private onLoaded() {
    this.loading = false;
    this.submitting = false;
    this.showPaymentDialog = false;
    this.showDeleteDialog = false;
    this.showOrderEditDialog = false;
  }

  private onDeleted() {
    this.$router.push({name: 'invoiceList'});
  }

  private onOrdersForInvoicingLoaded() {
    this.loadingOrders = false;
    this.selectedOrders = this.invoice && this.invoice.orders ? [...this.invoice.orders] : [];
  }

  private onError() {
    this.submitting = false;
    this.loading = false;
    this.loadingOrders = false;
  }
}
